import clsx from "clsx";

import { Icon, IconBox, Tooltip } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";
import Badge from "@/ui/atoms/Badge";
import Typography from "@/ui/atoms/Typography";

type TOptionWithType = {
    label: string;
    value: string | null;
    type?: "income" | "expense" | "non-operating";
};

// @HACK: We have to use magic string here because the only way `ag-grid` dropdowns communicate changes is via the string value of the cell
// and nullable values are already reserved for not mapped cells
const LOCAL_OMIT_ROW_VALUE__DO_NOT_EXPORT = "~~OMIT~~";
export const OMIT_ROW_OPTION = {
    label: "Row Omitted",
    value: LOCAL_OMIT_ROW_VALUE__DO_NOT_EXPORT,
};
export const isOmitOption = ({ value }: { value: string }) =>
    value === LOCAL_OMIT_ROW_VALUE__DO_NOT_EXPORT;

export type GridMappingsDropdownItemAccountsProps =
    JSX.IntrinsicElements["div"] & {
        value: string | null | undefined;
        valueFormatted: string | null | undefined;
        optionsAccounts: TOptionWithType[];
        displayRightIcon?: boolean;
        isRowOmitted: boolean;
        isAltered?: boolean;
        isReverted?: boolean;
    };

export const testIds = {
    main: "GridMappingsDropdownItemAccounts",
    selected: "GridMappingsDropdownItemAccountsWithRightIcon",
    altered: "GridMappingsDropdownItemAccounts-altered",
    text: "GridMappingsDropdownItemAccounts-text",
};
const GridMappingsDropdownItemAccounts = (
    props: GridMappingsDropdownItemAccountsProps,
) => {
    const {
        value,
        valueFormatted,
        optionsAccounts,
        displayRightIcon,
        isRowOmitted,
        isAltered,
        isReverted = false,
    } = props;

    const isRowNotMapped = !isRowOmitted && value === null;
    const isValueOutsideOptions =
        !isRowOmitted &&
        !isRowNotMapped &&
        !optionsAccounts.find(option => option.value === value);

    const type = optionsAccounts.find(option => option.value === value)?.type;
    const severity =
        type === "income"
            ? "success"
            : type === "expense"
              ? "error"
              : type === "non-operating"
                ? "inactive"
                : null;

    const testId = displayRightIcon === true ? testIds.selected : testIds.main;

    return (
        <Box
            data-testid={testId}
            className={clsx([testId])}
            sx={{
                position: "relative",
                width: "100%",
                height: "46px",
                gap: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingRight: isRowNotMapped ? "14px" : "16px",
                paddingLeft: isRowNotMapped ? "14px" : "16px",
                ...(isRowNotMapped
                    ? {
                          backgroundColor: "var(--proda-red-20)",
                          borderColor: "var(--proda-red-120)",
                          borderWidth: "2px",
                          borderStyle: "solid",
                      }
                    : {}),
            }}
        >
            {type && severity ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flexGrow: 0,
                        marginRight: "8px",
                    }}
                >
                    <Badge severity={severity}>
                        <Box
                            component={"span"}
                            sx={{
                                textTransform: "capitalize",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {type === "non-operating" ? "Below the line" : type}
                        </Box>
                    </Badge>
                </Box>
            ) : isRowOmitted ? (
                <Box
                    sx={{
                        flexBasis: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexGrow: 0,
                        fontSize: "12px",
                    }}
                >
                    <IconBox>
                        <Icon icon="ban" color="var(--proda-navy-blue)" />
                    </IconBox>
                </Box>
            ) : isRowNotMapped ? (
                <Box
                    sx={{
                        flexBasis: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexGrow: 0,
                        fontSize: "12px",
                    }}
                >
                    <IconBox>
                        <Icon icon="ban" color="var(--proda-red-120)" />
                    </IconBox>
                </Box>
            ) : null}
            <Box
                sx={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    verticalAlign: "middle",
                    textAlign: "left",
                    lineHeight: "24px",
                    paddingRight: "8px",
                    flexGrow: 1,
                }}
                data-testid={testIds.text}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color:
                            isRowOmitted || type === "non-operating"
                                ? "var(--text-color-disabled)"
                                : "var(--proda-black)",
                    }}
                >
                    {isRowNotMapped
                        ? "Select mapping"
                        : isRowOmitted
                          ? OMIT_ROW_OPTION.label
                          : isValueOutsideOptions
                            ? value
                            : valueFormatted}
                </Typography>
            </Box>
            {isAltered === true && !isReverted && (
                <Tooltip content="This cell was altered" placement="right-end">
                    <IconBox
                        style={{
                            position: "absolute",
                            top: "-5px",
                            right: "-2px",
                            transform: "rotate(-135deg)",
                        }}
                        data-testid={testIds.altered}
                    >
                        <Icon
                            icon="caret-down"
                            size="lg"
                            color={"var(--proda-purple)"}
                        />
                    </IconBox>
                </Tooltip>
            )}
            {isReverted === true && (
                <Tooltip
                    content="This cell was reverted to original value"
                    placement="right-end"
                >
                    <IconBox
                        style={{
                            position: "absolute",
                            top: "-5px",
                            right: "-2px",
                            transform: "rotate(-135deg)",
                        }}
                    >
                        <Icon
                            icon="caret-down"
                            size="lg"
                            color={"var(--proda-yellow-120)"}
                        />
                    </IconBox>
                </Tooltip>
            )}
            {displayRightIcon === true && (
                <Box
                    sx={{
                        flexBasis: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flexGrow: 0,
                        paddingRight: "8px",
                        fontSize: "12px",
                    }}
                >
                    <IconBox>
                        <Icon
                            icon="caret-down"
                            color={
                                isRowNotMapped
                                    ? "var(--proda-red-120)"
                                    : "var(--proda-white-150)"
                            }
                        />
                    </IconBox>
                </Box>
            )}
        </Box>
    );
};

export default GridMappingsDropdownItemAccounts;
